'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { products } from '@/data/products';

const FALLBACK_IMAGE = '/assets/images/no_image.png';

function ProductCardImage({ src, alt }: { src: string; alt: string }) {
  const [imgSrc, setImgSrc] = useState(src || FALLBACK_IMAGE);
  const [isLoading, setIsLoading] = useState(true);

  return (
    <div className="relative w-full overflow-hidden" style={{ height: '250px', backgroundColor: '#f8fafc' }}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted z-10">
          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      <Image
        src={imgSrc}
        alt={alt || 'Product image'}
        fill
        unoptimized
        className={`object-cover transition-opacity duration-300 group-hover:scale-105 transition-transform ${isLoading ? 'opacity-0' : 'opacity-100'}`}
        onLoad={() => setIsLoading(false)}
        onError={() => {
          setImgSrc(FALLBACK_IMAGE);
          setIsLoading(false);
        }}
        sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
      />
    </div>
  );
}

export default function ProductsPage() {
  const { t, language } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Category display name: use translation key if available, else raw category string
  const getCategoryLabel = (cat: string) => {
    const key = `catalog.cat.${cat.toLowerCase().replace(/[\s/()]+/g, '').replace(/é/g, 'e').replace(/è/g, 'e').replace(/ê/g, 'e').replace(/à/g, 'a').replace(/â/g, 'a').replace(/î/g, 'i').replace(/ô/g, 'o').replace(/û/g, 'u').replace(/ç/g, 'c')}`;
    const translated = t(key);
    return translated !== key ? translated : cat;
  };

  // Get unique categories in order of appearance
  const categories = useMemo(() => {
    const seen = new Set<string>();
    const ordered: string[] = [];
    products.forEach(p => {
      if (!seen.has(p.category)) {
        seen.add(p.category);
        ordered.push(p.category);
      }
    });
    return ['all', ...ordered];
  }, []);

  // Filter products based on search and category
  const filteredProducts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return products.filter(product => {
      const matchesSearch =
        query === '' || (product.commonNameFR ??'').toLowerCase().includes(query) ||
        (product.commonNameEN ?? '').toLowerCase().includes(query) ||
        (product.scientificName ?? '').toLowerCase().includes(query) ||
        (product.category ?? '').toLowerCase().includes(query) ||
        (product.slug ?? '').toLowerCase().includes(query);
      const matchesCategory =
        selectedCategory === 'all' || product.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory, language]);

  // Group filtered products by category
  const groupedProducts = useMemo(() => {
    const groups: { category: string; categoryKey: string; items: typeof filteredProducts }[] = [];
    const seen = new Set<string>();
    filteredProducts.forEach(p => {
      if (!seen.has(p.category)) {
        seen.add(p.category);
        groups.push({ category: p.category, categoryKey: p.categoryKey, items: [] });
      }
      groups.find(g => g.category === p.category)!.items.push(p);
    });
    return groups;
  }, [filteredProducts]);

  const labels = {
    en: {
      home: 'Home',
      products: 'Products',
      searchPlaceholder: 'Search by name or species...',
      allCategories: 'All Categories',
      viewDetails: 'View Details',
      results: 'products found',
      noResults: 'No products found',
      noResultsDesc: 'Try adjusting your search or filter.',
      category: 'Category',
      scientificName: 'Scientific name',
      commonNameFR: 'FR (Canada)',
      commonNameEN: 'EN (Canada)',
    },
    fr: {
      home: 'Accueil',
      products: 'Produits',
      searchPlaceholder: 'Rechercher par nom ou espèce...',
      allCategories: 'Toutes les Catégories',
      viewDetails: 'Voir les Détails',
      results: 'produits trouvés',
      noResults: 'Aucun produit trouvé',
      noResultsDesc: 'Essayez de modifier votre recherche ou filtre.',
      category: 'Catégorie',
      scientificName: 'Nom scientifique',
      commonNameFR: 'FR (Canada)',
      commonNameEN: 'EN (Canada)',
    },
    ar: {
      home: 'الرئيسية',
      products: 'المنتجات',
      searchPlaceholder: 'البحث عن المنتجات...',
      allCategories: 'جميع الفئات',
      viewDetails: 'عرض التفاصيل',
      results: 'منتج موجود',
      noResults: 'لا توجد منتجات',
      noResultsDesc: 'حاول تعديل البحث أو الفلتر.',
      category: 'الفئة',
      scientificName: 'الاسم العلمي',
      commonNameFR: 'FR (كندا)',
      commonNameEN: 'EN (كندا)',
    },
  };

  const l = labels[language as keyof typeof labels] || labels.en;

  return (
    <main className="min-h-screen bg-background">
      <Header />

      {/* Page Hero */}
      <section className="pt-32 pb-12 px-6 md:px-12 bg-muted border-b border-border">
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link href="/homepage" className="hover:text-primary transition-colors">
              {l.home}
            </Link>
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-muted-foreground/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-foreground font-medium">{l.products}</span>
          </nav>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <span className="inline-block px-4 py-2 bg-primary/10 text-primary text-sm font-semibold rounded-full mb-4">
                {t('catalog.badge')}
              </span>
              <h1 className="font-serif text-4xl md:text-5xl font-bold text-foreground mb-3">
                {l.products}
              </h1>
              <p className="text-muted-foreground text-lg max-w-xl">
                {t('catalog.subtitle')}
              </p>
            </div>
            <div className="text-sm text-muted-foreground font-medium shrink-0">
              {filteredProducts.length} {l.results}
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 px-6 md:px-12 bg-background border-b border-border sticky top-[72px] z-30 shadow-sm">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-[300px]">
            <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={l.searchPlaceholder}
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-border bg-card text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
            />
          </div>

          {/* Category Filter */}
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1 -mx-6 px-6 md:mx-0 md:px-0 md:flex-wrap md:overflow-visible">
              {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`shrink-0 px-3 py-2 rounded-xl text-xs font-medium transition-all duration-200 border whitespace-nowrap ${
                  selectedCategory === cat
                    ? 'bg-primary text-white border-primary shadow-sm'
                    : 'bg-card text-muted-foreground border-border hover:border-primary hover:text-primary'
                }`}
              >
                {cat === 'all' ? l.allCategories : getCategoryLabel(cat)}
              </button>
              ))}
          </div>
        </div>
      </section>

      {/* Products Grid - Grouped by Category */}
      <section className="py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-24">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                </svg>
              </div>
              <h3 className="font-serif text-xl font-bold text-foreground mb-2">{l.noResults}</h3>
              <p className="text-muted-foreground">{l.noResultsDesc}</p>
            </div>
          ) : (
            <div className="space-y-14">
              {groupedProducts.map(group => (
                <div key={group.category}>
                  {/* Category Header */}
                  <div className="flex items-center gap-4 mb-6">
                    <div className="h-px flex-1 bg-border" />
                    <h2 className="font-serif text-xl font-bold text-foreground px-2 whitespace-nowrap">
                      {(() => {
                        const translated = t(group.categoryKey);
                        return translated !== group.categoryKey ? translated : group.category;
                      })()}
                    </h2>
                    <div className="h-px flex-1 bg-border" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {group.items.map((product, index) => {
                      const productNumber = product.id ? parseInt(product.id.replace('product_', ''), 10) : index + 1;
                      const typeLabel = 'Type / Type :';
                      const originLabel = 'Origin / Origine :';
                      const typeValue = 'Wild / Sauvage';
                      const originValue = 'Mauritania / Mauritanie';

                      return (
                        <div
                          key={product.id}
                          className="bg-card border border-border rounded-2xl overflow-hidden group flex flex-col hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                          style={{ transitionDelay: `${Math.min(index * 20, 200)}ms` }}
                        >
                          {/* 1. TOP HEADER BAR - dark navy */}
                          <div className="px-3 py-2" style={{ backgroundColor: '#0B4F6C' }}>
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-xs text-white/70 whitespace-nowrap">{typeLabel}</span>
                              <span className="text-xs font-bold text-white text-right">{typeValue}</span>
                            </div>
                            <div className="flex items-center justify-between gap-2 mt-0.5">
                              <span className="text-xs text-white/70 whitespace-nowrap">{originLabel}</span>
                              <span className="text-xs font-bold text-white text-right">{originValue}</span>
                            </div>
                          </div>

                          {/* 2. MIDDLE INFO AREA */}
                          <div className="px-3 py-2.5 bg-card" style={{ minHeight: '75px' }}>
                            <p className="text-sm font-bold text-foreground uppercase tracking-wide leading-snug line-clamp-2">
                              {productNumber}. {product.scientificName?.toUpperCase()}
                            </p>
                            <p className="text-sm text-muted-foreground mt-0.5 line-clamp-1">
                              {product.commonNameEN} / {product.commonNameFR}
                            </p>
                          </div>

                          {/* 3. BOTTOM IMAGE AREA - full width, no padding */}
                          <Link
                            href={`/products/${product.slug}`}
                            className="block flex-1 overflow-hidden"
                          >
                            <ProductCardImage
                              src={product.image}
                              alt={product.alt || product.scientificName}
                            />
                          </Link>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}
